import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useCached } from "../lib/dataCache";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { Clock, Package as Pkg, CheckCircle2, History, Edit2, Printer, Plus, Minus, Trash2, List, AlertCircle, Inbox, XCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { VariantPicker, cartKey } from "../components/VariantPicker";
import { DpInput, calcDp } from "../components/DpInput";
import { ItemThumbs } from "../components/ItemThumbs";
import { addToQueue, getQueue, getQueued, updateQueued, QUEUE_EVENT } from "../lib/offlineQueue";

const fmtIDR = (n) => "Rp" + Number(n||0).toLocaleString("id-ID");
const itemLabel = (i) => `${i.product_name}${i.variant_name ? ` (${i.variant_name})` : ""} x${i.quantity}`;

const DAY_ID = ["Minggu","Senin","Selasa","Rabu","Kamis","Jumat","Sabtu"];
const MONTH_ID = ["Jan","Feb","Mar","Apr","Mei","Jun","Jul","Agt","Sep","Okt","Nov","Des"];
function fmtDateID(iso) {
  if (!iso) return "-";
  const d = new Date(iso + "T00:00:00");
  if (isNaN(d)) return iso;
  return `${DAY_ID[d.getDay()]}, ${d.getDate()} ${MONTH_ID[d.getMonth()]} ${d.getFullYear()}`;
}

function statusBadge(s) {
  const map = {
    reserved: { bg: "#DBEAFE", color: "#1E40AF", label: "Reserved" },
    picked_up: { bg: "#FEF3C7", color: "#92400E", label: "Disewa" },
    returned: { bg: "#DCFCE7", color: "#166534", label: "Selesai" },
  };
  const s2 = map[s] || { bg: "#F5F5F5", color: "#525252", label: s };
  return <span className="text-[10px] uppercase font-bold px-2 py-1 rounded" style={{background: s2.bg, color: s2.color}}>{s2.label}</span>;
}

function daysDiff(dateISO) {
  const d = new Date(dateISO + "T00:00:00");
  const today = new Date(); today.setHours(0,0,0,0);
  return Math.round((d - today) / 86400000);
}

function urgencyBadge(r) {
  if (r.status === "reserved") {
    const d = daysDiff(r.start_date);
    if (d < 0) return <span className="text-[10px] uppercase font-bold px-2 py-1 rounded bg-red-50 text-red-700 border border-red-200">Telat Ambil {Math.abs(d)}h</span>;
    if (d === 0) return <span className="text-[10px] uppercase font-bold px-2 py-1 rounded bg-red-100 text-red-800 border border-red-300">Ambil Hari Ini</span>;
    if (d === 1) return <span className="text-[10px] uppercase font-bold px-2 py-1 rounded bg-orange-50 text-orange-700 border border-orange-200">Besok Diambil</span>;
    if (d <= 7) return <span className="text-[10px] uppercase font-bold px-2 py-1 rounded bg-yellow-50 text-yellow-800 border border-yellow-200">{d} Hari Lagi Diambil</span>;
  }
  if (r.status === "picked_up") {
    const d = daysDiff(r.end_date);
    if (d < 0) return <span className="text-[10px] uppercase font-bold px-2 py-1 rounded bg-red-50 text-red-700 border border-red-200">Telat Kembali {Math.abs(d)}h</span>;
    if (d === 0) return <span className="text-[10px] uppercase font-bold px-2 py-1 rounded bg-red-100 text-red-800 border border-red-300">Kembali Hari Ini</span>;
    if (d === 1) return <span className="text-[10px] uppercase font-bold px-2 py-1 rounded bg-orange-50 text-orange-700 border border-orange-200">Besok Kembali</span>;
    if (d <= 7) return <span className="text-[10px] uppercase font-bold px-2 py-1 rounded bg-yellow-50 text-yellow-800 border border-yellow-200">{d} Hari Lagi Kembali</span>;
  }
  return null;
}

function RentalRow({ r, onAction, onReturn, onEdit, onEditFull, isOwner, onDelete }) {
  const nav = useNavigate();
  return (
    <div className="border border-neutral-200 rounded-md p-4 flex flex-wrap items-start justify-between gap-3 hover:bg-neutral-50 transition-colors" data-testid={`booking-row-${r.id}`}>
      <div className="flex-1 min-w-[240px]">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="font-display font-semibold text-neutral-900">{r.customer_name}</div>
          {statusBadge(r.status)}
          {urgencyBadge(r)}
        </div>
        <div className="text-xs font-mono-tab text-neutral-500 mt-0.5">{r.code} · {r.customer_whatsapp || "-"}</div>
        <div className="mt-2 text-xs text-neutral-600">{fmtDateID(r.start_date)} → {fmtDateID(r.end_date)} · <b>{r.days} hari</b></div>
        {r.picked_up_at && <div className="mt-0.5 text-[11px] text-neutral-500" data-testid={`pickup-time-${r.id}`}>Diambil: {new Date(r.picked_up_at).toLocaleString("id-ID")}</div>}
        <div className="mt-1 text-xs text-neutral-500">{r.items.map(itemLabel).join(", ")}</div>
        {r.acquisition_source && <div className="mt-1 text-[10px] text-neutral-400">Sumber: {r.acquisition_source}</div>}
        <ItemThumbs items={r.items} />
      </div>
      <div className="flex flex-col items-end gap-2">
        <div className="font-display font-bold text-lg font-mono-tab" style={{color: "var(--brand-primary)"}}>{fmtIDR(r.total)}</div>
        {r.late_fee > 0 && <div className="text-[10px] uppercase font-bold px-2 py-0.5 rounded bg-red-50 text-red-700 border border-red-200" data-testid={`late-fee-${r.id}`}>Denda {fmtIDR(r.late_fee)}</div>}
        {r.dp_amount > 0 && <div className="text-[10px] uppercase font-bold px-2 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200">DP {fmtIDR(r.dp_amount)} · Sisa {fmtIDR(r.remaining)}</div>}
        <div className="flex flex-wrap gap-1">
          {r.status !== "returned" && <Button size="sm" variant="outline" onClick={() => onEdit(r)} data-testid={`edit-booking-${r.id}`}><Edit2 className="w-3 h-3 mr-1" /> Edit Item</Button>}
          {isOwner && <Button size="sm" variant="outline" onClick={() => onEditFull(r)} data-testid={`edit-full-${r.id}`}><Edit2 className="w-3 h-3 mr-1" /> Edit Lengkap</Button>}
          <Button size="sm" variant="outline" onClick={() => nav(`/receipt/${r.id}`)}><Printer className="w-3 h-3 mr-1" /> Struk</Button>
          {r.status === "reserved" && <Button size="sm" onClick={() => onAction(r.id, "pickup")} className="text-white" style={{background: "var(--brand-primary)"}}>Diambil</Button>}
          {r.status === "picked_up" && <Button size="sm" onClick={() => onReturn(r)} className="text-white" style={{background: "var(--brand-primary)"}} data-testid={`return-${r.id}`}>Kembali</Button>}
          {isOwner && <Button size="sm" variant="ghost" className="text-red-600" onClick={() => onDelete(r.id)}><Trash2 className="w-3 h-3" /></Button>}
        </div>
      </div>
    </div>
  );
}

function ReturnDialog({ open, onOpenChange, rental, onSaved }) {
  const [calc, setCalc] = useState(null);
  const [fee, setFee] = useState(0);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open && rental) {
      setCalc(null);
      api.get(`/rentals/${rental.id}/late-fee`).then(r => { setCalc(r.data); setFee(r.data.late_fee || 0); })
        .catch(() => { setCalc({ enabled: false, hours_late: 0, late_fee: 0 }); setFee(0); });
    }
  }, [open, rental]);
  if (!rental) return null;
  const submit = async () => {
    setSaving(true);
    try {
      const body = calc?.enabled ? { late_fee: Number(fee) || 0 } : {};
      await api.post(`/rentals/${rental.id}/return`, body);
      toast.success("Transaksi diselesaikan");
      onOpenChange(false); onSaved();
    } catch { toast.error("Gagal menyelesaikan"); } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="return-dialog">
        <DialogHeader><DialogTitle>Selesaikan · {rental.code}</DialogTitle></DialogHeader>
        {!calc ? (
          <div className="py-6 text-center text-neutral-500"><Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Menghitung...</div>
        ) : (
          <div className="space-y-3 text-sm">
            {rental.picked_up_at && <div className="text-xs text-neutral-500">Diambil: {new Date(rental.picked_up_at).toLocaleString("id-ID")}</div>}
            {!calc.enabled ? (
              <div className="rounded bg-neutral-50 border border-neutral-200 p-3 text-neutral-600">Denda keterlambatan tidak aktif. Barang akan ditandai <b>Selesai</b>.</div>
            ) : calc.hours_late <= 0 ? (
              <div className="rounded bg-emerald-50 border border-emerald-200 p-3 text-emerald-800">Tepat waktu, tidak ada denda.</div>
            ) : (
              <div className="rounded bg-amber-50 border border-amber-200 p-3 text-amber-900">
                Telat <b>{calc.hours_late} jam</b>{calc.extra_days > 0 && <> · dihitung <b>+{calc.extra_days} hari</b> sewa</>}.<br />
                Saran denda: <b>{fmtIDR(calc.late_fee)}</b> {calc.mode === "auto" ? "(otomatis)" : "(bisa diubah)"}
              </div>
            )}
            {calc.enabled && (
              <div><Label>Denda Telat (Rp)</Label>
                <Input type="number" min="0" value={fee} onChange={e => setFee(e.target.value)} data-testid="return-late-fee" disabled={calc.mode === "auto"} />
                {calc.mode === "auto" && <p className="text-[10px] text-neutral-500 mt-1">Mode otomatis — denda mengikuti perhitungan sistem.</p>}
              </div>
            )}
            <Button onClick={submit} disabled={saving} className="w-full text-white" style={{background: "var(--brand-primary)"}} data-testid="return-confirm">{saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Menyimpan...</> : "Tandai Selesai"}</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function EditItemsDialog({ open, onOpenChange, rental, products, onSaved, isOwner }) {
  const [items, setItems] = useState([]);
  const [endDate, setEndDate] = useState("");
  const [q, setQ] = useState("");
  const [activeCat, setActiveCat] = useState("Semua");
  const [variantProduct, setVariantProduct] = useState(null);
  const [discount, setDiscount] = useState(0);
  const [discountType, setDiscountType] = useState("rupiah");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (rental) {
      setItems(rental.items.map(i => ({ ...i, key: cartKey(i.product_id, i.variant_id) })));
      setEndDate(rental.end_date); setActiveCat("Semua");
      setDiscount(rental.discount || 0); setDiscountType(rental.discount_type || "rupiah"); setNotes(rental.notes || "");
    }
  }, [rental]);
  if (!rental) return null;

  const UNCAT = "Tanpa Kategori";
  const chipList = (() => {
    const set = new Set();
    products.forEach(p => { if (p.category) set.add(p.category); });
    const arr = Array.from(set).sort();
    const hasUncat = products.some(p => !p.category);
    return ["Semua", ...arr, ...(hasUncat ? [UNCAT] : [])];
  })();

  const changeQty = (key, delta) => setItems(prev => prev.map(i => i.key === key ? {...i, quantity: Math.max(1, i.quantity + delta)} : i));
  const remove = (key) => setItems(prev => prev.filter(i => i.key !== key));
  const pushItem = (p, v) => {
    const key = cartKey(p.id, v?.id);
    setItems(prev => {
      const exist = prev.find(i => i.key === key);
      if (exist) return prev.map(i => i.key === key ? {...i, quantity: i.quantity + 1} : i);
      return [...prev, { key, product_id: p.id, product_name: p.name, daily_price: v?.daily_price || p.daily_price, quantity: 1, variant_id: v?.id || "", variant_name: v?.name || "", image_url: v?.image_url || p.image_url || "" }];
    });
  };
  const addItem = (p) => { if (p.variants?.length) setVariantProduct(p); else pushItem(p, null); };
  const save = async () => {
    if (saving) return;
    setSaving(true);
    const newItems = items.map(({ key, ...i }) => i);
    const payload = { items: newItems, end_date: endDate, discount: Number(discount) || 0, discount_type: discountType, notes };
    try {
      if (String(rental.id).startsWith("local-")) {
        const s = new Date(rental.start_date), e = new Date(endDate);
        const days = Math.max(1, Math.round((e - s) / 86400000));
        const subtotal = newItems.reduce((a, i) => a + i.daily_price * i.quantity * days, 0);
        const discAmount = discountType === "persen" ? subtotal * (Number(discount) || 0) / 100 : Number(discount) || 0;
        const total = Math.max(0, subtotal - discAmount);
        const { dp, remaining } = calcDp(total, rental.dp_value || 0, rental.dp_type || "rupiah");
        const rec = await getQueued(rental.id);
        if (rec) {
          const receipt = { ...rec.receipt, items: newItems, end_date: endDate, days, subtotal, discount: Number(discount) || 0, discount_type: discountType, discount_amount: discAmount, total, dp_amount: dp, remaining, notes };
          await updateQueued(rental.id, { payload: { ...rec.payload, items: newItems, end_date: endDate, discount: Number(discount) || 0, discount_type: discountType, notes }, receipt });
        }
        toast.success("Perubahan tersimpan (offline)"); onOpenChange(false); onSaved();
      } else if (!navigator.onLine) {
        await addToQueue({ local_id: "editq-" + rental.id, op: "edit_rental", rental_id: rental.id, payload, status: "pending", created_at: new Date().toISOString() });
        toast.success("Perubahan diantri · tersinkron saat online"); onOpenChange(false); onSaved();
      } else {
        await api.put(`/rentals/${rental.id}/items`, payload);
        toast.success("Booking diperbarui"); onOpenChange(false); onSaved();
      }
    } catch (err) { toast.error(err.response?.data?.detail || "Gagal update"); }
    finally { setSaving(false); }
  };
  const filtered = products.filter(p => {
    const catOk = activeCat === "Semua" ? true : activeCat === UNCAT ? !p.category : p.category === activeCat;
    const searchOk = !q || p.name.toLowerCase().includes(q.toLowerCase());
    return catOk && searchOk;
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Edit Item · {rental.code}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Mulai</Label><Input value={rental.start_date} disabled /></div>
            <div><Label>Selesai</Label><Input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} data-testid="edit-end-date" /></div>
          </div>
          <div>
            <Label>Item yang disewa</Label>
            <div className="space-y-2 mt-2">
              {items.map(i => (
                <div key={i.key} className="flex items-center gap-2 p-2 border border-neutral-200 rounded">
                  <div className="flex-1"><div className="text-sm font-medium">{i.product_name} {i.variant_name && <span className="text-[10px] uppercase font-bold text-blue-700 ml-1">{i.variant_name}</span>}</div><div className="text-xs font-mono-tab text-neutral-500">{fmtIDR(i.daily_price)}/hari</div></div>
                  {isOwner && <Button size="sm" variant="outline" className="h-7 w-7 p-0" onClick={() => changeQty(i.key, -1)} data-testid={`edit-qty-minus-${i.key}`}><Minus className="w-3 h-3" /></Button>}
                  <span className="w-6 text-center font-mono-tab text-sm">{i.quantity}</span>
                  <Button size="sm" variant="outline" className="h-7 w-7 p-0" onClick={() => changeQty(i.key, 1)} data-testid={`edit-qty-plus-${i.key}`}><Plus className="w-3 h-3" /></Button>
                  {isOwner && <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-600" onClick={() => remove(i.key)} data-testid={`edit-item-remove-${i.key}`}><Trash2 className="w-3 h-3" /></Button>}
                </div>
              ))}
            </div>
          </div>
          <div>
            <Label>Tambah Alat</Label>
            <Input placeholder="Cari alat..." value={q} onChange={e => setQ(e.target.value)} className="mt-1" />
            <div className="flex gap-2 overflow-x-auto pos-scroll -mx-1 px-1 py-1 mt-2" data-testid="edit-cat-chips">
              {chipList.map(c => (
                <button
                  key={c}
                  type="button"
                  data-testid={`edit-cat-chip-${c}`}
                  onClick={() => setActiveCat(c)}
                  className="shrink-0 px-3 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wider border transition-colors whitespace-nowrap"
                  style={activeCat === c
                    ? { background: "var(--brand-primary)", color: "#fff", borderColor: "var(--brand-primary)" }
                    : { background: "#fff", color: "#404040", borderColor: "#e5e5e5" }}
                >
                  {c}
                </button>
              ))}
            </div>
            <div className="mt-2 max-h-56 overflow-y-auto border border-neutral-200 rounded">
              {filtered.map(p => (
                <button key={p.id} type="button" onClick={() => addItem(p)} className="w-full flex justify-between items-center p-2 text-left hover:bg-neutral-50 border-b border-neutral-100 last:border-0">
                  <div><div className="text-sm font-medium">{p.name} {p.variants?.length > 0 && <span className="text-[9px] uppercase font-bold text-blue-700 ml-1">{p.variants.length} varian</span>}</div><div className="text-xs text-neutral-500">{p.category || "Tanpa Kategori"} · Sisa {p.available}</div></div>
                  <div className="text-sm font-mono-tab" style={{color: "var(--brand-primary)"}}>{fmtIDR(p.daily_price)}</div>
                </button>
              ))}
              {filtered.length === 0 && <div className="text-center text-xs text-neutral-500 py-4">Tidak ada alat</div>}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Diskon</Label>
              <div className="flex gap-2 mt-1">
                <div className="flex border border-neutral-200 rounded-md overflow-hidden">
                  <button type="button" onClick={() => setDiscountType("rupiah")} className={`px-3 text-sm font-semibold ${discountType === "rupiah" ? "text-white" : "text-neutral-600"}`} style={{background: discountType === "rupiah" ? "var(--brand-primary)" : "white"}}>Rp</button>
                  <button type="button" onClick={() => setDiscountType("persen")} className={`px-3 text-sm font-semibold ${discountType === "persen" ? "text-white" : "text-neutral-600"}`} style={{background: discountType === "persen" ? "var(--brand-primary)" : "white"}}>%</button>
                </div>
                <Input data-testid="edit-discount" type="number" value={discount} onChange={e => setDiscount(e.target.value)} className="flex-1" />
              </div>
            </div>
          </div>
          <div><Label>Catatan</Label><Textarea data-testid="edit-notes" value={notes} onChange={e => setNotes(e.target.value)} /></div>
          <Button data-testid="edit-items-save" onClick={save} disabled={saving} className="w-full text-white disabled:opacity-70" style={{background: "var(--brand-primary)"}}>{saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Menyimpan...</> : "Simpan Perubahan"}</Button>
        </div>
        <VariantPicker product={variantProduct} open={!!variantProduct} onOpenChange={(o) => !o && setVariantProduct(null)} onPick={pushItem} />
      </DialogContent>
    </Dialog>
  );
}

function EditFullDialog({ open, onOpenChange, rental, customers, onSaved }) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (rental) setForm({
      customer_id: rental.customer_id, start_date: rental.start_date, end_date: rental.end_date,
      deposit: rental.deposit, discount: rental.discount, discount_type: rental.discount_type || "rupiah",
      dp_value: rental.dp_value || 0, dp_type: rental.dp_type || "rupiah",
      notes: rental.notes || ""
    });
  }, [rental]);
  if (!rental || !form) return null;

  const save = async () => {
    if (saving) return;
    if (!navigator.onLine) return toast.error("Edit lengkap butuh internet.");
    setSaving(true);
    try {
      await api.patch(`/rentals/${rental.id}`, { ...form, dp_value: Number(form.dp_value) || 0 });
      toast.success("Transaksi diperbarui"); onOpenChange(false); onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || "Gagal"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Edit Lengkap · {rental.code}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Pelanggan</Label>
            <Select value={form.customer_id} onValueChange={v => setForm({...form, customer_id: v})}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{customers.map(c => <SelectItem key={c.id} value={c.id}>{c.full_name}</SelectItem>)}</SelectContent>
            </Select></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Mulai</Label><Input type="date" value={form.start_date} onChange={e => setForm({...form, start_date: e.target.value})} /></div>
            <div><Label>Selesai</Label><Input type="date" value={form.end_date} onChange={e => setForm({...form, end_date: e.target.value})} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Diskon</Label><Input type="number" value={form.discount} onChange={e => setForm({...form, discount: Number(e.target.value)})} /></div>
            <div><Label>Tipe Diskon</Label>
              <Select value={form.discount_type} onValueChange={v => setForm({...form, discount_type: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="rupiah">Rupiah</SelectItem><SelectItem value="persen">Persen</SelectItem></SelectContent>
              </Select></div>
          </div>
          <DpInput total={rental.total} value={form.dp_value} type={form.dp_type} onValue={v => setForm({...form, dp_value: v})} onType={t => setForm({...form, dp_type: t})} testIdPrefix="edit-dp" />
          <div><Label>Jaminan / Deposit</Label><Input type="number" value={form.deposit} onChange={e => setForm({...form, deposit: Number(e.target.value)})} /></div>
          <div><Label>Catatan</Label><Textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} /></div>
          <Button data-testid="edit-full-save" onClick={save} disabled={saving} className="w-full text-white disabled:opacity-70" style={{background: "var(--brand-primary)"}}>{saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Menyimpan...</> : "Simpan Perubahan"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Bookings() {
  const { user } = useAuth();
  const { settings } = useTheme();
  const isOwner = user?.role === "owner";
  const prepDays = settings?.booking_prep_days ?? 2;
  const [rentals, setRentals] = useCached("rentals", []);
  const [products, setProducts] = useCached("products", []);
  const [customers, setCustomers] = useCached("customers", []);
  const [pending, setPending] = useCached("pending_bookings", []);
  const [editOpen, setEditOpen] = useState(false);
  const [editFullOpen, setEditFullOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState("");
  const [returnRental, setReturnRental] = useState(null);
  const [returnOpen, setReturnOpen] = useState(false);
  const onReturn = (r) => {
    if (String(r.id).startsWith("local-")) return toast.error("Sinkronkan transaksi dulu sebelum menyelesaikan");
    setReturnRental(r); setReturnOpen(true);
  };

  const load = () => {
    const mergeLocals = async (serverRentals) => {
      const q = await getQueue().catch(() => []);
      const locals = q.filter(x => (x.op === "create_rental") && x.receipt).map(x => x.receipt);
      setRentals([...locals, ...serverRentals]);
    };
    api.get("/rentals").then(r => mergeLocals(r.data)).catch(() => mergeLocals([]));
    api.get("/products").then(r => setProducts(r.data)).catch(() => {});
    api.get("/customers").then(r => setCustomers(r.data)).catch(() => {});
    api.get("/pending-bookings").then(r => setPending(r.data)).catch(() => {});
  };
  useEffect(load, []);
  useEffect(() => {
    const onChange = () => load();
    window.addEventListener(QUEUE_EVENT, onChange);
    return () => window.removeEventListener(QUEUE_EVENT, onChange);
  }, []);

  const approvePending = async (id) => {
    if (busy) return;
    setBusy("approve" + id);
    try { await api.post(`/pending-bookings/${id}/approve`); toast.success("Booking disetujui & dibuat sebagai transaksi"); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Gagal"); }
    finally { setBusy(""); }
  };
  const rejectPending = async (id) => {
    if (busy) return;
    const reason = window.prompt("Alasan penolakan (opsional):", "");
    setBusy("reject" + id);
    try { await api.post(`/pending-bookings/${id}/reject`, { reason: reason || "" }); toast.success("Booking ditolak"); load(); }
    catch (err) { toast.error("Gagal"); }
    finally { setBusy(""); }
  };

  const action = async (id, kind) => {
    if (busy) return;
    setBusy(id + kind);
    try { await api.post(`/rentals/${id}/${kind}`); toast.success("Berhasil"); load(); }
    catch { toast.error("Gagal"); }
    finally { setBusy(""); }
  };
  const del = async (id) => {
    if (busy) return;
    if (!window.confirm("Pindahkan transaksi ini ke Sampah? Bisa dipulihkan dalam 30 hari.")) return;
    setBusy("del" + id);
    try { await api.delete(`/rentals/${id}`); toast.success("Dipindahkan ke Sampah"); load(); }
    catch { toast.error("Gagal"); }
    finally { setBusy(""); }
  };
  const openEdit = (r) => { setEditing(r); setEditOpen(true); };
  const openEditFull = (r) => { setEditing(r); setEditFullOpen(true); };

  const filter = (arr) => arr.filter(r =>
    r.customer_name.toLowerCase().includes(search.toLowerCase()) ||
    r.code.toLowerCase().includes(search.toLowerCase())
  );

  const soon = useMemo(() => filter(rentals.filter(r => {
    if (r.status !== "reserved") return false;
    const d = daysDiff(r.start_date);
    return d >= 0 && d <= prepDays;
  })), [rentals, search, prepDays]);
  const overdue = useMemo(() => filter(rentals.filter(r => {
    if (r.status === "reserved" && daysDiff(r.start_date) < 0) return true;
    if (r.status === "picked_up" && daysDiff(r.end_date) < 0) return true;
    return false;
  })), [rentals, search]);
  const active = useMemo(() => filter(rentals.filter(r => r.status === "picked_up")), [rentals, search]);
  const done = useMemo(() => filter(rentals.filter(r => r.status === "returned")), [rentals, search]);
  const all = useMemo(() => filter(rentals), [rentals, search]);
  const reservedRentals = useMemo(() => filter(rentals.filter(r => r.status === "reserved")), [rentals, search]);
  const pendingPublic = useMemo(() => pending.filter(p => p.status === "pending" && (
    p.customer_name.toLowerCase().includes(search.toLowerCase()) || p.code.toLowerCase().includes(search.toLowerCase())
  )), [pending, search]);
  const waitingCount = pendingPublic.length + reservedRentals.length;

  return (
    <div className="space-y-6" data-testid="bookings-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-neutral-500 font-semibold">Booking</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold text-neutral-900 mt-1">Manajemen Booking</h1>
          <p className="text-sm text-neutral-500 mt-1">Rentang siapkan: <b>H-{prepDays}</b> (bisa diubah di Pengaturan → Booking)</p>
        </div>
      </div>

      <Input placeholder="Cari nama / kode transaksi..." value={search} onChange={e => setSearch(e.target.value)} className="max-w-md" data-testid="booking-search" />

      <Tabs defaultValue="pending">
        <TabsList className="w-full md:w-fit overflow-x-auto flex">
          <TabsTrigger value="pending" data-testid="tab-pending"><Inbox className="w-4 h-4 mr-1" /> Menunggu ({waitingCount})</TabsTrigger>
          <TabsTrigger value="soon" data-testid="tab-soon"><Clock className="w-4 h-4 mr-1" /> Siapkan ({soon.length})</TabsTrigger>
          {overdue.length > 0 && <TabsTrigger value="overdue" data-testid="tab-overdue"><AlertCircle className="w-4 h-4 mr-1 text-red-600" /> Telat ({overdue.length})</TabsTrigger>}
          <TabsTrigger value="active" data-testid="tab-active"><Pkg className="w-4 h-4 mr-1" /> Disewa ({active.length})</TabsTrigger>
          <TabsTrigger value="done" data-testid="tab-done"><CheckCircle2 className="w-4 h-4 mr-1" /> Selesai ({done.length})</TabsTrigger>
          <TabsTrigger value="all" data-testid="tab-all"><List className="w-4 h-4 mr-1" /> Semua ({all.length})</TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history"><History className="w-4 h-4 mr-1" /> Riwayat ({done.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="pending" className="space-y-3 mt-4">
          <p className="text-sm text-neutral-500">Semua booking yang belum diambil — dari halaman publik <code>/book</code> maupun input kasir.</p>
          {pendingPublic.length > 0 && (
            <div className="text-xs uppercase tracking-wider text-neutral-500 font-semibold pt-2">Dari Web Publik ({pendingPublic.length})</div>
          )}
          {pendingPublic.map(p => (
            <div key={p.id} className="border border-neutral-200 rounded-md p-4 hover:bg-neutral-50" data-testid={`pending-${p.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="flex-1 min-w-[240px]">
                  <div className="flex items-center gap-2">
                    <div className="font-display font-semibold">{p.customer_name}</div>
                    <span className="text-[10px] uppercase font-bold px-2 py-1 rounded bg-blue-50 text-blue-700 border border-blue-200">Publik</span>
                  </div>
                  <div className="text-xs font-mono-tab text-neutral-500 mt-0.5">{p.code} · {p.whatsapp}</div>
                  <div className="mt-2 text-xs text-neutral-600">{fmtDateID(p.start_date)} → {fmtDateID(p.end_date)} · {p.days} hari</div>
                  <div className="mt-1 text-xs text-neutral-500">{p.items.map(itemLabel).join(", ")}</div>
                  <ItemThumbs items={p.items} />
                  {p.acquisition_source && <div className="mt-1 text-[10px] text-neutral-400">Sumber: {p.acquisition_source}</div>}
                  {p.notes && <div className="mt-1 text-xs text-neutral-500">Catatan: {p.notes}</div>}
                </div>
                <div className="flex flex-col items-end gap-2">
                  <div className="font-mono-tab font-bold text-lg" style={{color: "var(--brand-primary)"}}>~{fmtIDR(p.total_est)}</div>
                  <div className="flex gap-2">
                    <Button size="sm" onClick={() => approvePending(p.id)} disabled={busy === "approve" + p.id} className="text-white disabled:opacity-60" style={{background: "var(--brand-primary)"}} data-testid={`approve-${p.id}`}><CheckCircle2 className="w-3 h-3 mr-1" /> Setujui</Button>
                    <Button size="sm" variant="outline" className="text-red-600 disabled:opacity-60" onClick={() => rejectPending(p.id)} disabled={busy === "reject" + p.id} data-testid={`reject-${p.id}`}><XCircle className="w-3 h-3 mr-1" /> Tolak</Button>
                  </div>
                </div>
              </div>
            </div>
          ))}
          {reservedRentals.length > 0 && (
            <div className="text-xs uppercase tracking-wider text-neutral-500 font-semibold pt-2">Dari Kasir / POS ({reservedRentals.length})</div>
          )}
          {reservedRentals.map(r => (
            <div key={r.id} className="border border-neutral-200 rounded-md p-4 hover:bg-neutral-50" data-testid={`reserved-${r.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="flex-1 min-w-[240px]">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-display font-semibold text-neutral-900">{r.customer_name}</div>
                    <span className="text-[10px] uppercase font-bold px-2 py-1 rounded bg-amber-50 text-amber-800 border border-amber-200">Kasir</span>
                    {urgencyBadge(r)}
                  </div>
                  <div className="text-xs font-mono-tab text-neutral-500 mt-0.5">{r.code} · {r.customer_whatsapp || "-"}</div>
                  <div className="mt-2 text-xs text-neutral-600">{fmtDateID(r.start_date)} → {fmtDateID(r.end_date)} · <b>{r.days} hari</b></div>
                  <div className="mt-1 text-xs text-neutral-500">{r.items.map(itemLabel).join(", ")}</div>
                  <ItemThumbs items={r.items} />
                </div>
                <div className="flex flex-col items-end gap-2">
                  <div className="font-display font-bold text-lg font-mono-tab" style={{color: "var(--brand-primary)"}}>{fmtIDR(r.total)}</div>
                  {r.dp_amount > 0 && <div className="text-[10px] uppercase font-bold px-2 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200">DP {fmtIDR(r.dp_amount)} · Sisa {fmtIDR(r.remaining)}</div>}
                  <div className="flex flex-wrap gap-1">
                    <Button size="sm" variant="outline" onClick={() => openEdit(r)} data-testid={`edit-booking-${r.id}`}><Edit2 className="w-3 h-3 mr-1" /> Edit Item</Button>
                    {isOwner && <Button size="sm" variant="outline" onClick={() => openEditFull(r)}><Edit2 className="w-3 h-3 mr-1" /> Edit Lengkap</Button>}
                    <Button size="sm" onClick={() => action(r.id, "pickup")} disabled={busy === r.id + "pickup"} className="text-white disabled:opacity-60" style={{background: "var(--brand-primary)"}} data-testid={`pickup-${r.id}`}>Diambil</Button>
                  </div>
                </div>
              </div>
            </div>
          ))}
          {waitingCount === 0 && <div className="text-center py-12 text-neutral-500">Tidak ada booking menunggu</div>}
        </TabsContent>

        {[
          {v: "soon", data: soon, empty: `Tidak ada pickup H-${prepDays} atau lebih dekat`},
          {v: "overdue", data: overdue, empty: "Tidak ada yang telat"},
          {v: "active", data: active, empty: "Tidak ada barang sedang disewa"},
          {v: "done", data: done, empty: "Belum ada transaksi selesai"},
          {v: "all", data: all, empty: "Belum ada transaksi"},
          {v: "history", data: done, empty: "Belum ada riwayat"},
        ].map(t => (
          <TabsContent key={t.v} value={t.v} className="space-y-3 mt-4">
            {t.data.map(r => <RentalRow key={r.id} r={r} onAction={action} onReturn={onReturn} onEdit={openEdit} onEditFull={openEditFull} onDelete={del} isOwner={isOwner} />)}
            {t.data.length === 0 && <div className="text-center py-12 text-neutral-500">{t.empty}</div>}
          </TabsContent>
        ))}
      </Tabs>

      <EditItemsDialog open={editOpen} onOpenChange={setEditOpen} rental={editing} products={products} onSaved={load} isOwner={isOwner} />
      <EditFullDialog open={editFullOpen} onOpenChange={setEditFullOpen} rental={editing} customers={customers} onSaved={load} />
      <ReturnDialog open={returnOpen} onOpenChange={setReturnOpen} rental={returnRental} onSaved={load} />
    </div>
  );
}
